import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PlanMapper, Plan } from '@processpuzzle/platform-admin';
import { RUNTIME_CONFIGURATION, serviceRootOf } from '@processpuzzle/util';
import { map, Observable } from 'rxjs';

/** Whether an organization key can be claimed, as the form renders it while the visitor types. */
export interface KeyAvailability {
  key: string;
  available: boolean;
  /** Present when unavailable — a transloco key, not prose. */
  errorId?: string;
  suggestions: string[];
}

/**
 * The two public reads of the funnel: `GET /plans` and `GET /organizations/key-availability`.
 *
 * Against the **gateway's** root, not `PLATFORM_ADMIN_SERVICE_ROOT`. That is the whole reason this
 * service exists rather than reusing platform-admin's `PlanService`: that one points at
 * `platform/plans`, a staff-prefixed path on a host the Biz frontend cannot reach and should not be
 * taught about. Reusing it would have made the public pricing page a caller of the staff surface.
 *
 * Plain `HttpClient` rather than a `BaseEntityRestService`: neither answer is an entity with a
 * store. The plan catalogue is a list the funnel renders once, and a key check is a question with an
 * answer rather than a resource.
 *
 * The `Plan` model and its mapper come from `@processpuzzle/platform-admin`, because after the
 * billing schemas moved into `platform-shared-api.yaml` this is the same contract shape the staff
 * screen reads — and `Plan.price` formats money through `formatMinor`, which must have exactly one
 * implementation.
 */
@Injectable({ providedIn: 'root' })
export class PlanCatalogService {
  private readonly httpClient = inject(HttpClient);
  private readonly planMapper = inject(PlanMapper);
  private readonly baseUrl = serviceRootOf(inject(RUNTIME_CONFIGURATION), 'SUBSCRIPTION_SERVICE_ROOT');

  findAll(): Observable<Plan[]> {
    return this.httpClient.get<unknown[]>(`${this.baseUrl}/plans`).pipe(map((dtos) => (dtos ?? []).map((dto) => this.planMapper.fromDto(dto))));
  }

  /**
   * Availability of a key, as the visitor types.
   *
   * The key is sent unvalidated on purpose, matching the contract: partial or malformed input
   * ("m", "My Org") has to come back as `available: false` with an `errorId` the form can render,
   * where validating it here would mean the form deciding what a valid key is — a second definition
   * of a rule that provisioning also enforces, and the one that drifted would let a customer fill in
   * a key that then failed.
   */
  checkKey(key: string): Observable<KeyAvailability> {
    return this.httpClient
      .get<Partial<KeyAvailability>>(`${this.baseUrl}/organizations/key-availability`, { params: { key } })
      .pipe(map((dto) => ({ key: dto?.key ?? key, available: dto?.available ?? false, errorId: dto?.errorId, suggestions: dto?.suggestions ?? [] })));
  }
}
